/**
 * MicroCanvasEngine.ts
 * Ultra-lightweight pure TypeScript HTML5 Canvas 2D Isometric & 2D Scene Runtime (~0 KB external deps).
 * Replaces heavy 1.55 MB Phaser 3 bundle while preserving 100% API compatibility with
 * BootScene, HomeScene, MapScene, and PixelArtGenerator.
 *
 * Key iPhone 12 PWA Optimizations:
 * 1. Adaptive FPS (Smart Battery & Thermal Saver):
 *    - Automatically runs at 60 FPS during active movement, tweens, camera pans, or touch gestures.
 *    - Automatically throttles down to 10 FPS when the room is idle or when an HTML DOM modal is open.
 * 2. Isolated Canvas-Only Input:
 *    - Binds pointer/wheel events strictly to the <canvas> element (never window/document),
 *      eliminating click bleed-through from HTML DOM modals, TopHUD, and NavigationDock.
 * 3. Crisp Pixel Art Rendering:
 *    - Enforces imageSmoothingEnabled = false and integer/crisp transforms.
 */

export namespace Phaser {
  export const AUTO = 0;
  export const CANVAS = 1;

  export const Scale = {
    FIT: 1,
    CENTER_BOTH: 1,
  };

  export const Scenes = {
    Events: {
      SHUTDOWN: 'shutdown',
    },
  };

  export namespace Types {
    export namespace Core {
      export interface GameConfig {
        type?: number;
        parent?: string | HTMLElement;
        width?: number;
        height?: number;
        pixelArt?: boolean;
        resolution?: number;
        roundPixels?: boolean;
        backgroundColor?: string | number;
        scale?: {
          mode?: number;
          autoCenter?: number;
        };
        scene?: Array<new () => Scene>;
      }
    }
  }

  export namespace Input {
    export interface Pointer {
      x: number;
      y: number;
      worldX: number;
      worldY: number;
      isDown: boolean;
      button: number;
      downElement: HTMLElement | null;
      event?: Event;
    }
  }

  export const Math = {
    Clamp(value: number, min: number, max: number): number {
      return globalThis.Math.max(min, globalThis.Math.min(max, value));
    },
    Distance: {
      Between(x1: number, y1: number, x2: number, y2: number): number {
        return globalThis.Math.hypot(x2 - x1, y2 - y1);
      },
    },
  };

  export namespace Geom {
    export class Rectangle {
      public x: number;
      public y: number;
      public width: number;
      public height: number;

      constructor(x: number = 0, y: number = 0, width: number = 0, height: number = 0) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
      }

      public contains(x: number, y: number): boolean {
        return Rectangle.Contains(this, x, y);
      }

      public static Contains(rect: Rectangle, x: number, y: number): boolean {
        if (!rect || rect.width <= 0 || rect.height <= 0) return false;
        return (
          x >= rect.x &&
          x <= rect.x + rect.width &&
          y >= rect.y &&
          y <= rect.y + rect.height
        );
      }
    }
  }

  // =========================================================================
  // COLOR & EASING UTILITIES
  // =========================================================================

  function numberToCssColor(color: number | string, alpha: number = 1): string {
    if (typeof color === 'string') {
      return color;
    }
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    const clampedAlpha = Math.Clamp(alpha, 0, 1);
    if (clampedAlpha >= 1) {
      return `rgb(${r},${g},${b})`;
    }
    return `rgba(${r},${g},${b},${clampedAlpha})`;
  }

  function evaluateEase(easeName: string | undefined, t: number): number {
    const x = Math.Clamp(t, 0, 1);
    switch (easeName) {
      case 'Sine.easeInOut':
        return -(globalThis.Math.cos(globalThis.Math.PI * x) - 1) / 2;
      case 'Sine.easeOut':
        return globalThis.Math.sin((x * globalThis.Math.PI) / 2);
      case 'Quad.easeOut':
      case 'Power2':
        return 1 - (1 - x) * (1 - x);
      case 'Quad.easeInOut':
        return x < 0.5 ? 2 * x * x : 1 - globalThis.Math.pow(-2 * x + 2, 2) / 2;
      case 'Cubic.easeOut':
        return 1 - globalThis.Math.pow(1 - x, 3);
      case 'Linear':
      default:
        return x;
    }
  }

  // =========================================================================
  // EVENT EMITTER
  // =========================================================================

  export class EventEmitter {
    private listeners: Map<string, Array<{ fn: (...args: any[]) => void; once: boolean }>> =
      new Map();

    public on(event: string, fn: (...args: any[]) => void): this {
      const list = this.listeners.get(event) || [];
      list.push({ fn, once: false });
      this.listeners.set(event, list);
      return this;
    }

    public once(event: string, fn: (...args: any[]) => void): this {
      const list = this.listeners.get(event) || [];
      list.push({ fn, once: true });
      this.listeners.set(event, list);
      return this;
    }

    public off(event: string, fn?: (...args: any[]) => void): this {
      if (!fn) {
        this.listeners.delete(event);
        return this;
      }
      const list = this.listeners.get(event);
      if (list) {
        this.listeners.set(
          event,
          list.filter((item) => item.fn !== fn)
        );
      }
      return this;
    }

    public emit(event: string, ...args: any[]): boolean {
      const list = this.listeners.get(event);
      if (!list || list.length === 0) return false;
      const snapshot = [...list];
      const remaining = list.filter((item) => !item.once);
      this.listeners.set(event, remaining);
      for (const item of snapshot) {
        item.fn(...args);
      }
      return true;
    }

    public removeAllListeners(): void {
      this.listeners.clear();
    }
  }

  // =========================================================================
  // TEXTURE & ANIMATION MANAGERS
  // =========================================================================

  export interface TextureFrame {
    x: number;
    y: number;
    width: number;
    height: number;
  }

  export class CanvasTexture {
    public key: string;
    public canvas: HTMLCanvasElement;
    public hdCanvas: HTMLCanvasElement;
    public scale: number = 1;
    public frames: Map<number | string, TextureFrame> = new Map();

    constructor(keyOrCanvas: string | HTMLCanvasElement, maybeCanvas?: HTMLCanvasElement) {
      const actualCanvas =
        typeof keyOrCanvas === 'string' ? maybeCanvas! : keyOrCanvas;
      this.key = typeof keyOrCanvas === 'string' ? keyOrCanvas : '__anon';
      this.canvas = actualCanvas;
      this.hdCanvas = (actualCanvas as any)?.__hdCanvas || actualCanvas;
      this.scale =
        (actualCanvas as any)?.__scale ||
        (this.hdCanvas !== actualCanvas && actualCanvas?.width > 0
          ? this.hdCanvas.width / actualCanvas.width
          : 1);
      this.frames.set(0, {
        x: 0,
        y: 0,
        width: actualCanvas?.width || 16,
        height: actualCanvas?.height || 16,
      });
    }

    public add(
      name: number | string,
      _sourceIndex: number,
      x: number,
      y: number,
      width: number,
      height: number
    ): void {
      this.frames.set(name, { x, y, width, height });
    }

    public getFrame(frame?: number | string): TextureFrame {
      if (frame !== undefined && this.frames.has(frame)) {
        return this.frames.get(frame)!;
      }
      return (
        this.frames.get(0) || {
          x: 0,
          y: 0,
          width: this.canvas.width || 16,
          height: this.canvas.height || 16,
        }
      );
    }

    private tintCache: Map<string, HTMLCanvasElement> = new Map();

    public clearTintCache(): void {
      this.tintCache.clear();
    }

    public getTintedFrameCanvas(frameKey: number | string, tintColor: number): HTMLCanvasElement | null {
      if (typeof document === 'undefined') return null;
      const cacheKey = `${String(frameKey)}_${tintColor}`;
      if (this.tintCache.has(cacheKey)) {
        return this.tintCache.get(cacheKey)!;
      }
      const frame = this.getFrame(frameKey);
      try {
        const offscreen = document.createElement('canvas');
        offscreen.width = frame.width;
        offscreen.height = frame.height;
        const octx = offscreen.getContext('2d');
        if (octx) {
          octx.imageSmoothingEnabled = true;
          if ('imageSmoothingQuality' in octx) {
            octx.imageSmoothingQuality = 'high';
          }
          octx.clearRect(0, 0, frame.width, frame.height);
          octx.globalCompositeOperation = 'source-over';
          octx.drawImage(
            this.canvas,
            frame.x,
            frame.y,
            frame.width,
            frame.height,
            0,
            0,
            frame.width,
            frame.height
          );
          octx.globalCompositeOperation = 'source-atop';
          octx.fillStyle = numberToCssColor(tintColor, 0.38);
          octx.fillRect(0, 0, frame.width, frame.height);
          octx.globalCompositeOperation = 'source-over';
        }

        if (this.hdCanvas && this.scale > 1) {
          const hdOffscreen = document.createElement('canvas');
          hdOffscreen.width = frame.width * this.scale;
          hdOffscreen.height = frame.height * this.scale;
          const hdoctx = hdOffscreen.getContext('2d');
          if (hdoctx) {
            hdoctx.imageSmoothingEnabled = true;
            if ('imageSmoothingQuality' in hdoctx) {
              hdoctx.imageSmoothingQuality = 'high';
            }
            hdoctx.clearRect(0, 0, hdOffscreen.width, hdOffscreen.height);
            hdoctx.globalCompositeOperation = 'source-over';
            hdoctx.drawImage(
              this.hdCanvas,
              frame.x * this.scale,
              frame.y * this.scale,
              frame.width * this.scale,
              frame.height * this.scale,
              0,
              0,
              hdOffscreen.width,
              hdOffscreen.height
            );
            hdoctx.globalCompositeOperation = 'source-atop';
            hdoctx.fillStyle = numberToCssColor(tintColor, 0.38);
            hdoctx.fillRect(0, 0, hdOffscreen.width, hdOffscreen.height);
            hdoctx.globalCompositeOperation = 'source-over';
          }
          (offscreen as any).__hdCanvas = hdOffscreen;
          (offscreen as any).__scale = this.scale;
        }

        this.tintCache.set(cacheKey, offscreen);
        return offscreen;
      } catch {
        return null;
      }
    }
  }

  export class TextureManager {
    private textures: Map<string, CanvasTexture> = new Map();

    public exists(key: string): boolean {
      return this.textures.has(key);
    }

    public remove(key: string): void {
      this.textures.delete(key);
    }

    public addCanvas(key: string, canvas: HTMLCanvasElement): CanvasTexture | null {
      if (!canvas) return null;
      const tex = new CanvasTexture(key, canvas);
      this.textures.set(key, tex);
      return tex;
    }

    public get(key: string): CanvasTexture | undefined {
      return this.textures.get(key);
    }
  }

  export interface AnimationConfig {
    key: string;
    frames: Array<{ key: string; frame: number }>;
    frameRate: number;
    repeat: number;
  }

  export class AnimationManager {
    private anims: Map<string, AnimationConfig> = new Map();

    public exists(key: string): boolean {
      return this.anims.has(key);
    }

    public create(config: AnimationConfig): AnimationConfig {
      this.anims.set(config.key, config);
      return config;
    }

    public get(key: string): AnimationConfig | undefined {
      return this.anims.get(key);
    }
  }

  // =========================================================================
  // GAME OBJECTS
  // =========================================================================

  export namespace GameObjects {
    export class GameObject extends EventEmitter {
      public scene: Scene;
      public x: number = 0;
      public y: number = 0;
      public width: number = 0;
      public height: number = 0;
      public originX: number = 0.5;
      public originY: number = 0.5;
      public scaleX: number = 1;
      public scaleY: number = 1;
      public rotation: number = 0;
      public alpha: number = 1;
      public depth: number = 0;
      public visible: boolean = true;
      public scrollFactorX: number = 1;
      public scrollFactorY: number = 1;
      public flipX: boolean = false;
      public tint: number | null = null;
      public isInteractive: boolean = false;
      public parentContainer: Container | null = null;
      public destroyed: boolean = false;
      private customData: Map<string, any> = new Map();

      constructor(scene: Scene, x: number = 0, y: number = 0) {
        super();
        this.scene = scene;
        this.x = x;
        this.y = y;
      }

      public setPosition(x: number, y?: number): this {
        this.x = x;
        if (y !== undefined) this.y = y;
        return this;
      }

      public setOrigin(x: number, y?: number): this {
        this.originX = x;
        this.originY = y !== undefined ? y : x;
        return this;
      }

      public setDepth(depth: number): this {
        this.depth = depth;
        return this;
      }

      public setScale(sx: number, sy?: number): this {
        this.scaleX = sx;
        this.scaleY = sy !== undefined ? sy : sx;
        return this;
      }

      public setRotation(radians: number): this {
        this.rotation = radians;
        return this;
      }

      public setAlpha(alpha: number): this {
        this.alpha = Math.Clamp(alpha, 0, 1);
        return this;
      }

      public setVisible(visible: boolean): this {
        this.visible = visible;
        return this;
      }

      public setScrollFactor(x: number, y?: number): this {
        this.scrollFactorX = x;
        this.scrollFactorY = y !== undefined ? y : x;
        return this;
      }

      public setFlipX(flip: boolean): this {
        this.flipX = flip;
        return this;
      }

      public setTint(color: number): this {
        this.tint = color;
        return this;
      }

      public clearTint(): this {
        this.tint = null;
        return this;
      }

      public setData(key: string, value: any): this {
        this.customData.set(key, value);
        return this;
      }

      public getData(key: string): any {
        return this.customData.get(key);
      }

      public setSize(width: number, height: number): this {
        this.width = width;
        this.height = height;
        return this;
      }

      public setInteractive(_config?: any): this {
        this.isInteractive = true;
        return this;
      }

      public update(_deltaMs: number): void {}

      public render(_ctx: CanvasRenderingContext2D): void {}

      public destroy(): void {
        this.destroyed = true;
        this.visible = false;
        this.removeAllListeners();
        if (this.parentContainer) {
          this.parentContainer.remove(this);
        }
        this.scene._removeGameObject(this);
      }
    }

    export class Image extends GameObject {
      public textureKey: string;
      public frameKey: number | string = 0;

      constructor(scene: Scene, x: number, y: number, textureKey: string, frameKey: number | string = 0) {
        super(scene, x, y);
        this.textureKey = textureKey;
        this.frameKey = frameKey;
        this.syncDimensions();
      }

      protected syncDimensions(): void {
        const tex = this.scene.textures.get(this.textureKey);
        if (tex) {
          const f = tex.getFrame(this.frameKey);
          this.width = f.width;
          this.height = f.height;
        }
      }

      public setTexture(key: string, frame: number | string = 0): this {
        this.textureKey = key;
        this.frameKey = frame;
        this.syncDimensions();
        return this;
      }

      public render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible || this.alpha <= 0) return;
        const tex = this.scene.textures.get(this.textureKey);
        if (!tex || !tex.canvas) return;
        const frame = tex.getFrame(this.frameKey);

        ctx.save();
        ctx.translate(this.x, this.y);
        if (this.rotation !== 0) {
          ctx.rotate(this.rotation);
        }
        ctx.scale(this.flipX ? -this.scaleX : this.scaleX, this.scaleY);
        ctx.globalAlpha *= this.alpha;

        const drawX = -frame.width * this.originX;
        const drawY = -frame.height * this.originY;

        if (this.tint !== null) {
          const tintedCanvas = tex.getTintedFrameCanvas(this.frameKey, this.tint);
          if (tintedCanvas) {
            const hdTint = (tintedCanvas as any).__hdCanvas || tintedCanvas;
            const s = (tintedCanvas as any).__scale || 1;
            ctx.drawImage(
              hdTint,
              0,
              0,
              frame.width * s,
              frame.height * s,
              drawX,
              drawY,
              frame.width,
              frame.height
            );
            ctx.restore();
            return;
          }
        }

        const hdSource = tex.hdCanvas || tex.canvas;
        const s = tex.scale || 1;
        ctx.drawImage(
          hdSource,
          frame.x * s,
          frame.y * s,
          frame.width * s,
          frame.height * s,
          drawX,
          drawY,
          frame.width,
          frame.height
        );
        ctx.restore();
      }
    }

    export class Sprite extends Image {
      public currentAnimKey: string | null = null;
      private animElapsedMs: number = 0;
      private animFrameIndex: number = 0;

      public play(animKey: string, ignoreIfPlaying: boolean = true): this {
        if (ignoreIfPlaying && this.currentAnimKey === animKey) {
          return this;
        }
        this.currentAnimKey = animKey;
        this.animElapsedMs = 0;
        this.animFrameIndex = 0;

        const anim = this.scene.anims.get(animKey);
        if (anim && anim.frames.length > 0) {
          const first = anim.frames[0];
          this.setTexture(first.key, first.frame);
        }
        return this;
      }

      public update(deltaMs: number): void {
        if (!this.currentAnimKey) return;
        const anim = this.scene.anims.get(this.currentAnimKey);
        if (!anim || anim.frames.length <= 1 || anim.frameRate <= 0) return;

        const frameDurationMs = 1000 / anim.frameRate;
        this.animElapsedMs += deltaMs;
        if (this.animElapsedMs >= frameDurationMs) {
          const steps = globalThis.Math.floor(this.animElapsedMs / frameDurationMs);
          this.animElapsedMs %= frameDurationMs;
          this.animFrameIndex = (this.animFrameIndex + steps) % anim.frames.length;
          const current = anim.frames[this.animFrameIndex];
          if (current) {
            this.frameKey = current.frame;
          }
        }
      }
    }

    export class Rectangle extends GameObject {
      public fillColor: number = 0xffffff;
      public fillAlpha: number = 1;
      public strokeWidth: number = 0;
      public strokeColor: number = 0xffffff;
      public strokeAlpha: number = 1;

      constructor(
        scene: Scene,
        x: number,
        y: number,
        width: number,
        height: number,
        fillColor: number = 0xffffff,
        fillAlpha: number = 1
      ) {
        super(scene, x, y);
        this.width = width;
        this.height = height;
        this.fillColor = fillColor;
        this.fillAlpha = fillAlpha;
      }

      public setFillStyle(color: number = 0xffffff, alpha: number = 1): this {
        this.fillColor = color;
        this.fillAlpha = alpha;
        return this;
      }

      public setStrokeStyle(lineWidth: number, color: number = 0xffffff, alpha: number = 1): this {
        this.strokeWidth = lineWidth;
        this.strokeColor = color;
        this.strokeAlpha = alpha;
        return this;
      }

      public render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible || this.alpha <= 0) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(this.scaleX, this.scaleY);
        ctx.globalAlpha *= this.alpha;

        const rx = -this.width * this.originX;
        const ry = -this.height * this.originY;

        if (this.fillAlpha > 0) {
          ctx.fillStyle = numberToCssColor(this.fillColor, this.fillAlpha);
          ctx.fillRect(rx, ry, this.width, this.height);
        }
        if (this.strokeWidth > 0 && this.strokeAlpha > 0) {
          ctx.lineWidth = this.strokeWidth;
          ctx.strokeStyle = numberToCssColor(this.strokeColor, this.strokeAlpha);
          ctx.strokeRect(rx, ry, this.width, this.height);
        }
        ctx.restore();
      }
    }

    export class Circle extends GameObject {
      public radius: number;
      public fillColor: number;
      public fillAlpha: number;
      public strokeWidth: number = 0;
      public strokeColor: number = 0xffffff;
      public strokeAlpha: number = 1;

      constructor(
        scene: Scene,
        x: number,
        y: number,
        radius: number,
        fillColor: number = 0xffffff,
        fillAlpha: number = 1
      ) {
        super(scene, x, y);
        this.radius = radius;
        this.width = radius * 2;
        this.height = radius * 2;
        this.fillColor = fillColor;
        this.fillAlpha = fillAlpha;
      }

      public setStrokeStyle(lineWidth: number, color: number = 0xffffff, alpha: number = 1): this {
        this.strokeWidth = lineWidth;
        this.strokeColor = color;
        this.strokeAlpha = alpha;
        return this;
      }

      public render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible || this.alpha <= 0) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(this.scaleX, this.scaleY);
        ctx.globalAlpha *= this.alpha;

        ctx.beginPath();
        ctx.arc(0, 0, this.radius, 0, globalThis.Math.PI * 2);
        if (this.fillAlpha > 0) {
          ctx.fillStyle = numberToCssColor(this.fillColor, this.fillAlpha);
          ctx.fill();
        }
        if (this.strokeWidth > 0 && this.strokeAlpha > 0) {
          ctx.lineWidth = this.strokeWidth;
          ctx.strokeStyle = numberToCssColor(this.strokeColor, this.strokeAlpha);
          ctx.stroke();
        }
        ctx.restore();
      }
    }

    export class Ellipse extends GameObject {
      public fillColor: number;
      public fillAlpha: number;

      constructor(
        scene: Scene,
        x: number,
        y: number,
        width: number,
        height: number,
        fillColor: number = 0xffffff,
        fillAlpha: number = 1
      ) {
        super(scene, x, y);
        this.width = width;
        this.height = height;
        this.fillColor = fillColor;
        this.fillAlpha = fillAlpha;
      }

      public render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible || this.alpha <= 0) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(this.scaleX, this.scaleY);
        ctx.globalAlpha *= this.alpha;

        ctx.beginPath();
        ctx.ellipse(0, 0, this.width / 2, this.height / 2, 0, 0, globalThis.Math.PI * 2);
        ctx.fillStyle = numberToCssColor(this.fillColor, this.fillAlpha);
        ctx.fill();
        ctx.restore();
      }
    }

    export interface TextStyle {
      fontFamily?: string;
      fontSize?: string;
      fontStyle?: string;
      color?: string;
      align?: string;
    }

    export class Text extends GameObject {
      public text: string;
      public style: TextStyle;

      constructor(scene: Scene, x: number, y: number, text: string, style: TextStyle = {}) {
        super(scene, x, y);
        this.text = text;
        this.style = style;
      }

      public setText(text: string): this {
        this.text = text;
        return this;
      }

      public render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible || this.alpha <= 0 || !this.text) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(this.scaleX, this.scaleY);
        ctx.globalAlpha *= this.alpha;

        const fontStyle = this.style.fontStyle || 'normal';
        const fontSize = this.style.fontSize || '11px';
        const fontFamily = this.style.fontFamily || 'system-ui, -apple-system, sans-serif';
        ctx.font = `${fontStyle} ${fontSize} ${fontFamily}`;
        ctx.fillStyle = this.style.color || '#ffffff';
        ctx.textAlign =
          this.originX === 0.5 ? 'center' : this.originX >= 1 ? 'right' : 'left';
        ctx.textBaseline =
          this.originY === 0.5 ? 'middle' : this.originY >= 1 ? 'bottom' : 'top';

        const lines = String(this.text).split('\n');
        const pxSize = parseFloat(fontSize) || 11;
        const lineHeight = pxSize * 1.25;
        const startY = -((lines.length - 1) * lineHeight) * this.originY;

        for (let i = 0; i < lines.length; i++) {
          ctx.fillText(lines[i], 0, startY + i * lineHeight);
        }
        ctx.restore();
      }
    }

    export class Graphics extends GameObject {
      private ops: Array<(ctx: CanvasRenderingContext2D) => void> = [];

      public clear(): this {
        this.ops = [];
        return this;
      }

      public lineStyle(lineWidth: number, color: number, alpha: number = 1): this {
        const css = numberToCssColor(color, alpha);
        this.ops.push((ctx) => {
          ctx.lineWidth = lineWidth;
          ctx.strokeStyle = css;
        });
        return this;
      }

      public fillStyle(color: number, alpha: number = 1): this {
        const css = numberToCssColor(color, alpha);
        this.ops.push((ctx) => {
          ctx.fillStyle = css;
        });
        return this;
      }

      public beginPath(): this {
        this.ops.push((ctx) => ctx.beginPath());
        return this;
      }

      public moveTo(x: number, y: number): this {
        this.ops.push((ctx) => ctx.moveTo(x, y));
        return this;
      }

      public lineTo(x: number, y: number): this {
        this.ops.push((ctx) => ctx.lineTo(x, y));
        return this;
      }

      public closePath(): this {
        this.ops.push((ctx) => ctx.closePath());
        return this;
      }

      public fillPath(): this {
        this.ops.push((ctx) => ctx.fill());
        return this;
      }

      public strokePath(): this {
        this.ops.push((ctx) => ctx.stroke());
        return this;
      }

      public lineBetween(x1: number, y1: number, x2: number, y2: number): this {
        this.ops.push((ctx) => {
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        });
        return this;
      }

      public fillCircle(x: number, y: number, radius: number): this {
        const r = radius > 0.1 ? radius : 0.1;
        this.ops.push((ctx) => {
          ctx.beginPath();
          ctx.arc(x, y, r, 0, globalThis.Math.PI * 2);
          ctx.fill();
        });
        return this;
      }

      public strokeCircle(x: number, y: number, radius: number): this {
        const r = radius > 0.1 ? radius : 0.1;
        this.ops.push((ctx) => {
          ctx.beginPath();
          ctx.arc(x, y, r, 0, globalThis.Math.PI * 2);
          ctx.stroke();
        });
        return this;
      }

      public fillRect(x: number, y: number, width: number, height: number): this {
        this.ops.push((ctx) => {
          ctx.fillRect(x, y, width, height);
        });
        return this;
      }

      public strokeRect(x: number, y: number, width: number, height: number): this {
        this.ops.push((ctx) => {
          ctx.strokeRect(x, y, width, height);
        });
        return this;
      }

      public fillRoundedRect(x: number, y: number, width: number, height: number, radius: number = 0): this {
        this.ops.push((ctx) => {
          if (ctx.roundRect) {
            ctx.beginPath();
            ctx.roundRect(x, y, width, height, radius);
            ctx.fill();
          } else {
            ctx.fillRect(x, y, width, height);
          }
        });
        return this;
      }

      public strokeRoundedRect(x: number, y: number, width: number, height: number, radius: number = 0): this {
        this.ops.push((ctx) => {
          if (ctx.roundRect) {
            ctx.beginPath();
            ctx.roundRect(x, y, width, height, radius);
            ctx.stroke();
          } else {
            ctx.strokeRect(x, y, width, height);
          }
        });
        return this;
      }

      public fillTriangle(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): this {
        this.ops.push((ctx) => {
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.lineTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.closePath();
          ctx.fill();
        });
        return this;
      }

      public strokeTriangle(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): this {
        this.ops.push((ctx) => {
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.lineTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.closePath();
          ctx.stroke();
        });
        return this;
      }

      public render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible || this.alpha <= 0 || this.ops.length === 0) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(this.scaleX, this.scaleY);
        ctx.globalAlpha *= this.alpha;
        for (const op of this.ops) {
          op(ctx);
        }
        ctx.restore();
      }
    }

    export class Container extends GameObject {
      public list: GameObject[] = [];

      constructor(scene: Scene, x: number = 0, y: number = 0, children?: GameObject[]) {
        super(scene, x, y);
        if (children && children.length > 0) {
          this.add(children);
        }
      }

      public add(childOrChildren: GameObject | GameObject[]): this {
        const items = Array.isArray(childOrChildren) ? childOrChildren : [childOrChildren];
        for (const item of items) {
          if (!item) continue;
          item.parentContainer = this;
          if (!this.list.includes(item)) {
            this.list.push(item);
          }
        }
        return this;
      }

      public remove(child: GameObject): this {
        const idx = this.list.indexOf(child);
        if (idx !== -1) {
          this.list.splice(idx, 1);
          child.parentContainer = null;
        }
        return this;
      }

      public render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible || this.alpha <= 0) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(this.scaleX, this.scaleY);
        ctx.globalAlpha *= this.alpha;

        for (const child of this.list) {
          if (!child.destroyed && child.visible) {
            child.render(ctx);
          }
        }
        ctx.restore();
      }

      public override destroy(): void {
        const copy = [...this.list];
        this.list = [];
        for (const child of copy) {
          child.parentContainer = null;
          child.destroy();
        }
        super.destroy();
      }
    }
  }

  // =========================================================================
  // TWEENS
  // =========================================================================

  export namespace Tweens {
    export interface TweenConfig {
      targets: any;
      duration?: number;
      ease?: string;
      yoyo?: boolean;
      repeat?: number;
      onUpdate?: () => void;
      onComplete?: () => void;
      [prop: string]: any;
    }

    const RESERVED_KEYS = new Set([
      'targets',
      'duration',
      'ease',
      'yoyo',
      'repeat',
      'onUpdate',
      'onComplete',
    ]);

    export class Tween {
      public targets: any[];
      public duration: number;
      public ease: string;
      public yoyo: boolean;
      public repeat: number;
      public onUpdate?: () => void;
      public onComplete?: () => void;
      public isPlaying: boolean = true;

      private elapsedMs: number = 0;
      private forward: boolean = true;
      private repeatsDone: number = 0;
      private propEntries: Array<{
        target: any;
        key: string;
        startVal: number;
        endVal: number;
      }> = [];

      constructor(config: TweenConfig) {
        this.targets = Array.isArray(config.targets) ? config.targets : [config.targets];
        this.duration = globalThis.Math.max(1, config.duration ?? 250);
        this.ease = config.ease || 'Linear';
        this.yoyo = !!config.yoyo;
        this.repeat = config.repeat ?? 0;
        this.onUpdate = config.onUpdate;
        this.onComplete = config.onComplete;

        for (const target of this.targets) {
          if (!target) continue;
          for (const key of Object.keys(config)) {
            if (RESERVED_KEYS.has(key)) continue;
            const rawVal = config[key];
            const endVal = typeof rawVal === 'function' ? Number(rawVal(target)) : Number(rawVal);
            const startVal = Number(target[key] ?? 0);
            if (Number.isFinite(startVal) && Number.isFinite(endVal)) {
              this.propEntries.push({ target, key, startVal, endVal });
            }
          }
        }
      }

      public stop(): this {
        this.isPlaying = false;
        return this;
      }

      public step(deltaMs: number): void {
        if (!this.isPlaying) return;
        this.elapsedMs += deltaMs;

        while (this.isPlaying && this.elapsedMs >= this.duration) {
          this.elapsedMs -= this.duration;

          if (this.yoyo && this.forward) {
            this.forward = false;
          } else {
            this.forward = true;
            if (this.repeat === -1) {
              // Infinite loop
            } else if (this.repeatsDone < this.repeat) {
              this.repeatsDone++;
            } else {
              // Completed final cycle
              this.applyProgress(this.yoyo ? 0 : 1);
              this.isPlaying = false;
              if (this.onUpdate) this.onUpdate();
              if (this.onComplete) this.onComplete();
              return;
            }
          }
        }

        if (!this.isPlaying) return;
        const rawT = this.elapsedMs / this.duration;
        const progress = this.forward ? rawT : 1 - rawT;
        this.applyProgress(progress);
        if (this.onUpdate) this.onUpdate();
      }

      private applyProgress(t: number): void {
        const eased = evaluateEase(this.ease, t);
        for (const entry of this.propEntries) {
          if (entry.target && !entry.target.destroyed) {
            entry.target[entry.key] = entry.startVal + (entry.endVal - entry.startVal) * eased;
          }
        }
      }
    }

    export class TweenManager {
      private tweens: Tween[] = [];

      public add(config: TweenConfig): Tween {
        const tween = new Tween(config);
        this.tweens.push(tween);
        return tween;
      }

      public update(deltaMs: number): void {
        if (this.tweens.length === 0) return;
        const active = [...this.tweens];
        for (const t of active) {
          if (t.isPlaying) {
            t.step(deltaMs);
          }
        }
        this.tweens = this.tweens.filter((t) => t.isPlaying);
      }

      public hasActiveNonLoopingTweens(): boolean {
        return this.tweens.some((t) => t.isPlaying && t.repeat !== -1);
      }

      public getActiveCount(): number {
        return this.tweens.filter((t) => t.isPlaying).length;
      }

      public clear(): void {
        for (const t of this.tweens) {
          t.stop();
        }
        this.tweens = [];
      }
    }
  }

  // =========================================================================
  // CAMERA
  // =========================================================================

  export class Camera {
    public width: number;
    public height: number;
    public zoom: number = 1;
    public scrollX: number = 0;
    public scrollY: number = 0;

    private bounds: Geom.Rectangle | null = null;
    private followTarget: { x: number; y: number } | null = null;
    private lerpX: number = 1;
    private lerpY: number = 1;

    private panActive: boolean = false;
    private panStartX: number = 0;
    private panStartY: number = 0;
    private panTargetX: number = 0;
    private panTargetY: number = 0;
    private panDuration: number = 0;
    private panElapsed: number = 0;
    private panEase: string = 'Power2';

    public fadeAlpha: number = 0;
    private fadeActive: boolean = false;
    private fadeFrom: number = 0;
    private fadeTo: number = 0;
    private fadeDuration: number = 0;
    private fadeElapsed: number = 0;

    constructor(width: number = 390, height: number = 700) {
      this.width = width;
      this.height = height;
    }

    public setBounds(x: number, y: number, width: number, height: number): this {
      this.bounds = new Geom.Rectangle(x, y, width, height);
      return this;
    }

    public setZoom(zoom: number): this {
      this.zoom = globalThis.Math.max(0.1, zoom);
      return this;
    }

    public centerOn(x: number, y: number): this {
      this.scrollX = x - this.width / 2;
      this.scrollY = y - this.height / 2;
      return this;
    }

    public startFollow(
      target: { x: number; y: number },
      _roundPixels: boolean = true,
      lerpX: number = 1,
      lerpY: number = 1
    ): this {
      this.followTarget = target;
      this.lerpX = lerpX;
      this.lerpY = lerpY;
      return this;
    }

    public stopFollow(): this {
      this.followTarget = null;
      return this;
    }

    public pan(x: number, y: number, duration: number = 250, ease: string = 'Power2'): this {
      this.panStartX = this.scrollX;
      this.panStartY = this.scrollY;
      this.panTargetX = x - this.width / 2;
      this.panTargetY = y - this.height / 2;
      this.panDuration = globalThis.Math.max(1, duration);
      this.panElapsed = 0;
      this.panEase = ease;
      this.panActive = true;
      return this;
    }

    public fadeOut(duration: number = 300, _r: number = 0, _g: number = 0, _b: number = 0): this {
      this.fadeFrom = this.fadeAlpha;
      this.fadeTo = 1;
      this.fadeDuration = globalThis.Math.max(1, duration);
      this.fadeElapsed = 0;
      this.fadeActive = true;
      return this;
    }

    public fadeIn(duration: number = 300, _r: number = 0, _g: number = 0, _b: number = 0): this {
      this.fadeFrom = 1;
      this.fadeTo = 0;
      this.fadeDuration = globalThis.Math.max(1, duration);
      this.fadeElapsed = 0;
      this.fadeActive = true;
      return this;
    }

    public getWorldPoint(screenX: number, screenY: number): { x: number; y: number } {
      const halfW = this.width / 2;
      const halfH = this.height / 2;
      const z = this.zoom || 1;
      return {
        x: (screenX - halfW) / z + this.scrollX + halfW,
        y: (screenY - halfH) / z + this.scrollY + halfH,
      };
    }

    public isAnimating(): boolean {
      return this.panActive || this.fadeActive;
    }

    public update(deltaMs: number): void {
      if (this.panActive) {
        this.panElapsed += deltaMs;
        const t = Math.Clamp(this.panElapsed / this.panDuration, 0, 1);
        const eased = evaluateEase(this.panEase, t);
        this.scrollX = this.panStartX + (this.panTargetX - this.panStartX) * eased;
        this.scrollY = this.panStartY + (this.panTargetY - this.panStartY) * eased;
        if (t >= 1) {
          this.panActive = false;
        }
      } else if (this.followTarget) {
        const targetScrollX = this.followTarget.x - this.width / 2;
        const targetScrollY = this.followTarget.y - this.height / 2;
        const frames = globalThis.Math.max(1, deltaMs / 16.667);
        const fx = this.lerpX >= 1 ? 1 : 1 - globalThis.Math.pow(1 - this.lerpX, frames);
        const fy = this.lerpY >= 1 ? 1 : 1 - globalThis.Math.pow(1 - this.lerpY, frames);
        this.scrollX += (targetScrollX - this.scrollX) * fx;
        this.scrollY += (targetScrollY - this.scrollY) * fy;
        if (globalThis.Math.hypot(targetScrollX - this.scrollX, targetScrollY - this.scrollY) < 0.25) {
          this.scrollX = targetScrollX;
          this.scrollY = targetScrollY;
        }
      }

      if (this.bounds) {
        const minScrollX = this.bounds.x;
        const maxScrollX = this.bounds.x + this.bounds.width - this.width;
        const minScrollY = this.bounds.y;
        const maxScrollY = this.bounds.y + this.bounds.height - this.height;
        if (maxScrollX > minScrollX) {
          this.scrollX = Math.Clamp(this.scrollX, minScrollX, maxScrollX);
        }
        if (maxScrollY > minScrollY) {
          this.scrollY = Math.Clamp(this.scrollY, minScrollY, maxScrollY);
        }
      }

      if (this.fadeActive) {
        this.fadeElapsed += deltaMs;
        const t = Math.Clamp(this.fadeElapsed / this.fadeDuration, 0, 1);
        this.fadeAlpha = this.fadeFrom + (this.fadeTo - this.fadeFrom) * t;
        if (t >= 1) {
          this.fadeActive = false;
        }
      }
    }
  }

  // =========================================================================
  // SCENE INPUT & FACTORY PLUGINS
  // =========================================================================

  export class InputPlugin extends EventEmitter {
    public pointer1: Input.Pointer = {
      x: 0,
      y: 0,
      worldX: 0,
      worldY: 0,
      isDown: false,
      button: 0,
      downElement: null,
    };
    public pointer2: Input.Pointer = {
      x: 0,
      y: 0,
      worldX: 0,
      worldY: 0,
      isDown: false,
      button: 0,
      downElement: null,
    };

    public addPointer(_count: number = 1): void {}
  }

  export class TimePlugin {
    private timers: Array<ReturnType<typeof setTimeout>> = [];

    public delayedCall(delayMs: number, callback: () => void): void {
      const id = setTimeout(callback, delayMs);
      this.timers.push(id);
    }

    public clear(): void {
      for (const id of this.timers) {
        clearTimeout(id);
      }
      this.timers = [];
    }
  }

  export class GameObjectFactory {
    private scene: Scene;

    constructor(scene: Scene) {
      this.scene = scene;
    }

    public image(x: number, y: number, textureKey: string): GameObjects.Image {
      const img = new GameObjects.Image(this.scene, x, y, textureKey);
      this.scene._registerGameObject(img);
      return img;
    }

    public sprite(x: number, y: number, textureKey: string): GameObjects.Sprite {
      const spr = new GameObjects.Sprite(this.scene, x, y, textureKey);
      this.scene._registerGameObject(spr);
      return spr;
    }

    public rectangle(
      x: number,
      y: number,
      width: number,
      height: number,
      fillColor: number = 0xffffff,
      fillAlpha: number = 1
    ): GameObjects.Rectangle {
      const rect = new GameObjects.Rectangle(this.scene, x, y, width, height, fillColor, fillAlpha);
      this.scene._registerGameObject(rect);
      return rect;
    }

    public circle(
      x: number,
      y: number,
      radius: number,
      fillColor: number = 0xffffff,
      fillAlpha: number = 1
    ): GameObjects.Circle {
      const cir = new GameObjects.Circle(this.scene, x, y, radius, fillColor, fillAlpha);
      this.scene._registerGameObject(cir);
      return cir;
    }

    public ellipse(
      x: number,
      y: number,
      width: number,
      height: number,
      fillColor: number = 0xffffff,
      fillAlpha: number = 1
    ): GameObjects.Ellipse {
      const ell = new GameObjects.Ellipse(this.scene, x, y, width, height, fillColor, fillAlpha);
      this.scene._registerGameObject(ell);
      return ell;
    }

    public text(
      x: number,
      y: number,
      text: string,
      style?: GameObjects.TextStyle
    ): GameObjects.Text {
      const txt = new GameObjects.Text(this.scene, x, y, text, style);
      this.scene._registerGameObject(txt);
      return txt;
    }

    public graphics(): GameObjects.Graphics {
      const g = new GameObjects.Graphics(this.scene, 0, 0);
      this.scene._registerGameObject(g);
      return g;
    }

    public container(
      x: number = 0,
      y: number = 0,
      children?: GameObjects.GameObject[]
    ): GameObjects.Container {
      const c = new GameObjects.Container(this.scene, x, y, children);
      this.scene._registerGameObject(c);
      return c;
    }
  }

  // =========================================================================
  // SCENE & SCENE MANAGER
  // =========================================================================

  export class Scene {
    public sceneKey: string;
    public game!: Game;
    public textures!: TextureManager;
    public anims!: AnimationManager;
    public add!: GameObjectFactory;
    public tweens!: Tweens.TweenManager;
    public cameras!: { main: Camera };
    public input!: InputPlugin;
    public events!: EventEmitter;
    public time!: TimePlugin;
    public scene!: {
      start: (key: string) => void;
      key: string;
    };

    private displayList: GameObjects.GameObject[] = [];

    constructor(config: string | { key: string }) {
      this.sceneKey = typeof config === 'string' ? config : config.key;
      this.initSubsystems();
    }

    public initSubsystems(game?: Game): void {
      if (game) {
        this.game = game;
        this.textures = game.textures;
        this.anims = game.anims;
      } else {
        this.textures = this.textures || new TextureManager();
        this.anims = this.anims || new AnimationManager();
      }
      this.add = new GameObjectFactory(this);
      this.tweens = new Tweens.TweenManager();
      this.cameras = {
        main: new Camera(this.game?.width || 390, this.game?.height || 700),
      };
      this.input = new InputPlugin();
      this.events = new EventEmitter();
      this.time = new TimePlugin();
      this.scene = {
        key: this.sceneKey,
        start: (targetKey: string) => {
          this.game?.scene.start(targetKey);
        },
      };
    }

    public create(): void {}

    public _registerGameObject(obj: GameObjects.GameObject): void {
      this.displayList.push(obj);
    }

    public _removeGameObject(obj: GameObjects.GameObject): void {
      const idx = this.displayList.indexOf(obj);
      if (idx !== -1) {
        this.displayList.splice(idx, 1);
      }
    }

    public getDisplayList(): GameObjects.GameObject[] {
      return this.displayList;
    }

    public _shutdown(): void {
      this.events.emit(Scenes.Events.SHUTDOWN);
      this.events.removeAllListeners();
      this.input.removeAllListeners();
      this.tweens.clear();
      this.time.clear();
      this.displayList = [];
    }

    public _update(deltaMs: number): void {
      this.tweens.update(deltaMs);
      for (const obj of this.displayList) {
        if (!obj.destroyed && obj.visible) {
          obj.update(deltaMs);
        }
      }
      this.cameras.main.update(deltaMs);
    }

    public _render(ctx: CanvasRenderingContext2D, bgColor: string): void {
      const cam = this.cameras.main;
      const w = cam.width;
      const h = cam.height;

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      if ('imageSmoothingQuality' in ctx) {
        ctx.imageSmoothingQuality = 'high';
      }
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, w, h);

      // Sort top-level display objects by Painter's Algorithm depth (lowest depth first)
      const rootObjects = this.displayList.filter((o) => !o.destroyed && !o.parentContainer && o.visible);
      rootObjects.sort((a, b) => a.depth - b.depth);

      const halfW = w / 2;
      const halfH = h / 2;
      const zoom = cam.zoom || 1;

      for (const obj of rootObjects) {
        if (obj.scrollFactorX === 0 && obj.scrollFactorY === 0) {
          // Screen-space HUD/Banner element
          obj.render(ctx);
        } else {
          // Camera-transformed world-space element
          ctx.save();
          ctx.translate(halfW, halfH);
          ctx.scale(zoom, zoom);
          ctx.translate(-(cam.scrollX + halfW), -(cam.scrollY + halfH));
          obj.render(ctx);
          ctx.restore();
        }
      }

      if (cam.fadeAlpha > 0) {
        ctx.fillStyle = `rgba(0,0,0,${Math.Clamp(cam.fadeAlpha, 0, 1)})`;
        ctx.fillRect(0, 0, w, h);
      }

      ctx.restore();
    }

    public _dispatchInteractivePointerUp(pointer: Input.Pointer): void {
      const candidates = this.displayList.filter(
        (o) => !o.destroyed && o.visible && o.isInteractive && o.width > 0 && o.height > 0
      );
      candidates.sort((a, b) => b.depth - a.depth);

      for (const obj of candidates) {
        const px = obj.scrollFactorX === 0 ? pointer.x : pointer.worldX;
        const py = obj.scrollFactorY === 0 ? pointer.y : pointer.worldY;
        const baseX = obj.parentContainer ? obj.x + obj.parentContainer.x : obj.x;
        const baseY = obj.parentContainer ? obj.y + obj.parentContainer.y : obj.y;
        const rect = new Geom.Rectangle(
          baseX - obj.width * obj.originX,
          baseY - obj.height * obj.originY,
          obj.width,
          obj.height
        );
        if (Geom.Rectangle.Contains(rect, px, py)) {
          obj.emit('pointerup', pointer);
          break;
        }
      }
    }
  }

  export class SceneManager {
    private game: Game;
    private scenes: Map<string, Scene> = new Map();
    private activeScene: Scene | null = null;

    constructor(game: Game, sceneClasses: Array<new () => Scene> = []) {
      this.game = game;
      for (const SceneCls of sceneClasses) {
        const instance = new SceneCls();
        instance.initSubsystems(game);
        this.scenes.set(instance.sceneKey, instance);
      }
    }

    public add(SceneCls: new () => Scene, autoStart: boolean = false): Scene {
      const instance = new SceneCls();
      instance.initSubsystems(this.game);
      this.scenes.set(instance.sceneKey, instance);
      if (autoStart) {
        this.start(instance.sceneKey);
      }
      return instance;
    }

    public start(key: string): Scene | null {
      const next = this.scenes.get(key);
      if (!next) return null;

      if (this.activeScene) {
        this.activeScene._shutdown();
      }

      next.initSubsystems(this.game);
      this.activeScene = next;
      next.create();
      this.game.markInteraction();
      this.game.render();
      return next;
    }

    public getScene(key: string): Scene | undefined {
      return this.scenes.get(key);
    }

    public getActiveScene(): Scene | null {
      return this.activeScene;
    }
  }

  // =========================================================================
  // GAME INSTANCE & ADAPTIVE FPS BATTERY SAVER LOOP (iPhone 12)
  // =========================================================================

  export class Game {
    public static readonly ACTIVE_FPS = 60;
    public static readonly IDLE_FPS = 10;
    public static readonly ACTIVE_COOLDOWN_MS = 2000;

    public width: number;
    public height: number;
    public resolution: number;
    public backgroundColor: string;
    public canvas: HTMLCanvasElement | null = null;
    public ctx: CanvasRenderingContext2D | null = null;
    public textures: TextureManager;
    public anims: AnimationManager;
    public scene: SceneManager;

    private rafId: number | null = null;
    private lastFrameTime: number = 0;
    private lastInteractionTime: number = 0;
    private isDestroyed: boolean = false;
    private activeTouches: Map<number, { x: number; y: number }> = new Map();

    constructor(config: Types.Core.GameConfig = {}) {
      this.width = config.width || 390;
      this.height = config.height || 700;
      const dpr = typeof window !== 'undefined' ? globalThis.Math.max(2, window.devicePixelRatio || 2) : 2;
      this.resolution = config.resolution !== undefined
        ? config.resolution
        : (config.pixelArt === true ? 1 : dpr);
      this.backgroundColor =
        typeof config.backgroundColor === 'number'
          ? numberToCssColor(config.backgroundColor, 1)
          : config.backgroundColor || '#0d0d16';

      this.textures = new TextureManager();
      this.anims = new AnimationManager();

      this.mountCanvas(config.parent);
      this.scene = new SceneManager(this, config.scene || []);

      if (config.scene && config.scene.length > 0) {
        const firstScene = Array.from(this.scene['scenes'].keys())[0];
        if (firstScene) {
          this.scene.start(firstScene);
        }
      }

      this.startLoop();
    }

    private mountCanvas(parent?: string | HTMLElement): void {
      if (typeof document === 'undefined') return;
      const container =
        typeof parent === 'string'
          ? document.getElementById(parent)
          : parent || document.getElementById('game-container');

      const canvas = document.createElement('canvas');
      canvas.width = globalThis.Math.round(this.width * this.resolution);
      canvas.height = globalThis.Math.round(this.height * this.resolution);
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.objectFit = 'contain';
      canvas.style.touchAction = 'none';
      canvas.style.display = 'block';
      canvas.style.userSelect = 'none';

      if (container) {
        container.innerHTML = '';
        container.appendChild(canvas);
      }

      this.canvas = canvas;
      try {
        this.ctx = canvas.getContext('2d');
        if (this.ctx) {
          this.ctx.imageSmoothingEnabled = true;
          if ('imageSmoothingQuality' in this.ctx) {
            this.ctx.imageSmoothingQuality = 'high';
          }
        }
      } catch {
        this.ctx = null;
      }

      this.bindCanvasScopedInput(canvas);
    }

    /**
     * Scopes pointer and wheel events strictly to the <canvas> element.
     * Never attaches window/document listeners, preventing DOM modal button click bleed-through.
     */
    private bindCanvasScopedInput(canvas: HTMLCanvasElement): void {
      const toCanvasCoords = (clientX: number, clientY: number): { x: number; y: number } => {
        const rect = canvas.getBoundingClientRect();
        if (!rect || rect.width <= 0 || rect.height <= 0) {
          return { x: clientX, y: clientY };
        }
        return {
          x: ((clientX - rect.left) / rect.width) * this.width,
          y: ((clientY - rect.top) / rect.height) * this.height,
        };
      };

      const updatePointer = (
        ptr: Input.Pointer,
        clientX: number,
        clientY: number,
        isDown: boolean,
        button: number,
        evt: Event
      ): void => {
        const pt = toCanvasCoords(clientX, clientY);
        const active = this.scene.getActiveScene();
        const worldPt = active
          ? active.cameras.main.getWorldPoint(pt.x, pt.y)
          : { x: pt.x, y: pt.y };

        ptr.x = pt.x;
        ptr.y = pt.y;
        ptr.worldX = worldPt.x;
        ptr.worldY = worldPt.y;
        ptr.isDown = isDown;
        ptr.button = button;
        ptr.downElement = canvas;
        ptr.event = evt;
      };

      canvas.addEventListener('pointerdown', (ev: PointerEvent) => {
        this.markInteraction();
        const active = this.scene.getActiveScene();
        if (!active) return;

        const ptrId = ev.pointerId ?? 1;
        this.activeTouches.set(ptrId, { x: ev.clientX, y: ev.clientY });
        if (this.activeTouches.size >= 2) {
          const pts = Array.from(this.activeTouches.values());
          updatePointer(active.input.pointer1, pts[0].x, pts[0].y, true, 0, ev);
          updatePointer(active.input.pointer2, pts[1].x, pts[1].y, true, 0, ev);
        } else {
          updatePointer(active.input.pointer1, ev.clientX, ev.clientY, true, ev.button ?? 0, ev);
        }

        active.input.emit('pointerdown', active.input.pointer1);
        this.render();
      });

      canvas.addEventListener('pointermove', (ev: PointerEvent) => {
        const active = this.scene.getActiveScene();
        if (!active) return;

        const ptrId = ev.pointerId ?? 1;
        if (this.activeTouches.has(ptrId)) {
          this.activeTouches.set(ptrId, { x: ev.clientX, y: ev.clientY });
        }

        if (this.activeTouches.size >= 2) {
          this.markInteraction();
          const pts = Array.from(this.activeTouches.values());
          updatePointer(active.input.pointer1, pts[0].x, pts[0].y, true, 0, ev);
          updatePointer(active.input.pointer2, pts[1].x, pts[1].y, true, 0, ev);
        } else {
          const isDown = active.input.pointer1.isDown;
          if (isDown) this.markInteraction();
          updatePointer(active.input.pointer1, ev.clientX, ev.clientY, isDown, ev.button ?? 0, ev);
        }

        active.input.emit('pointermove', active.input.pointer1);
      });

      canvas.addEventListener('pointerup', (ev: PointerEvent) => {
        this.markInteraction();
        const active = this.scene.getActiveScene();
        if (!active) return;

        const ptrId = ev.pointerId ?? 1;
        this.activeTouches.delete(ptrId);
        active.input.pointer2.isDown = false;

        if (this.activeTouches.size > 0) {
          const remaining = Array.from(this.activeTouches.values())[0];
          updatePointer(active.input.pointer1, remaining.x, remaining.y, true, 0, ev);
          this.render();
          return;
        }

        updatePointer(active.input.pointer1, ev.clientX, ev.clientY, false, ev.button ?? 0, ev);
        active.input.emit('pointerup', active.input.pointer1);
        active._dispatchInteractivePointerUp(active.input.pointer1);
        this.render();
      });

      canvas.addEventListener('pointercancel', (ev: PointerEvent) => {
        this.markInteraction();
        const active = this.scene.getActiveScene();
        this.activeTouches.clear();
        if (!active) return;

        active.input.pointer2.isDown = false;
        updatePointer(active.input.pointer1, ev.clientX, ev.clientY, false, ev.button ?? 0, ev);
        active.input.emit('pointercancel', active.input.pointer1);
        this.render();
      });

      canvas.addEventListener(
        'wheel',
        (ev: WheelEvent) => {
          this.markInteraction();
          const active = this.scene.getActiveScene();
          if (!active) return;
          updatePointer(active.input.pointer1, ev.clientX, ev.clientY, false, 0, ev);
          active.input.emit('wheel', active.input.pointer1, [], ev.deltaX, ev.deltaY);
          this.render();
        },
        { passive: true }
      );
    }

    public markInteraction(): void {
      this.lastInteractionTime = Date.now();
    }

    /**
     * Computes target FPS for iPhone 12 Smart Battery Saver:
     * - Returns 10 FPS when a DOM modal is open (#modal-container.active), document is hidden,
     *   or when the scene has been completely idle for >2000ms after arrival/activity.
     * - Returns 60 FPS during active character walking, finite tweens, camera pans, touch input,
     *   and for an additional 2000ms (2s) after the character arrives so camera lerp finishes smoothly.
     */
    public getCurrentTargetFps(): number {
      if (typeof document !== 'undefined') {
        if (document.hidden) return Game.IDLE_FPS;
        const modalContainer = document.getElementById('modal-container');
        if (modalContainer && modalContainer.classList.contains('active')) {
          return Game.IDLE_FPS;
        }
      }

      const active = this.scene.getActiveScene();
      if (!active) return Game.IDLE_FPS;

      if (
        active.input.pointer1.isDown ||
        active.input.pointer2.isDown ||
        active.tweens.hasActiveNonLoopingTweens() ||
        active.cameras.main.isAnimating() ||
        Date.now() - this.lastInteractionTime < Game.ACTIVE_COOLDOWN_MS
      ) {
        return Game.ACTIVE_FPS;
      }

      return Game.IDLE_FPS;
    }

    public step(deltaMs: number): void {
      const active = this.scene.getActiveScene();
      if (!active) return;
      const hadActiveMotion =
        active.input.pointer1.isDown ||
        active.input.pointer2.isDown ||
        active.tweens.hasActiveNonLoopingTweens() ||
        active.cameras.main.isAnimating();
      active._update(deltaMs);
      if (
        hadActiveMotion ||
        active.tweens.hasActiveNonLoopingTweens() ||
        active.cameras.main.isAnimating()
      ) {
        this.markInteraction();
      }
      this.render();
    }

    public render(): void {
      if (!this.ctx) return;
      const active = this.scene.getActiveScene();
      if (!active) return;
      this.ctx.save();
      if (this.resolution !== 1 && this.ctx.setTransform) {
        this.ctx.setTransform(this.resolution, 0, 0, this.resolution, 0, 0);
      }
      active._render(this.ctx, this.backgroundColor);
      this.ctx.restore();
    }

    private startLoop(): void {
      if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        return;
      }

      const tick = (timestamp: number) => {
        if (this.isDestroyed) return;
        const targetFps = this.getCurrentTargetFps();
        const minIntervalMs = 1000 / targetFps;
        const elapsed = timestamp - this.lastFrameTime;

        if (elapsed >= minIntervalMs - 1) {
          const deltaMs = this.lastFrameTime > 0 ? globalThis.Math.min(elapsed, 200) : 16.67;
          this.lastFrameTime = timestamp;
          this.step(deltaMs);
        }

        this.rafId = window.requestAnimationFrame(tick);
      };

      this.rafId = window.requestAnimationFrame(tick);
    }

    public destroy(removeCanvas: boolean = false): void {
      this.isDestroyed = true;
      if (this.rafId !== null && typeof window !== 'undefined' && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      const active = this.scene.getActiveScene();
      if (active) {
        active._shutdown();
      }
      if (removeCanvas && this.canvas && this.canvas.parentNode) {
        this.canvas.parentNode.removeChild(this.canvas);
      }
    }
  }
}

export default Phaser;
